import { useEffect, useState } from 'react';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';
import { TERMS } from '../../../shared/constant/terms';
import { confirmAction } from '../../../shared/utils/dialogs';
import { clearDraft, formatDraftTime, readDraft, writeDraft } from '../../../shared/utils/drafts';
import CommentModal from './CommentModal';

// The form's values as loaded from the assessment being edited (or blank for a new one).
function initialValues(assessment, defaultTerm, defaultSection) {
  return {
    title: assessment?.title ?? '',
    term: String(assessment?.term ?? defaultTerm ?? 1),
    maxScore: assessment ? String(Number(assessment.max_score)) : '',
    date: assessment?.assessment_date ?? new Date().toLocaleDateString('en-CA'),
    section: assessment ? assessment.section ?? '' : defaultSection ?? '',
    scores: Object.fromEntries((assessment?.assessment_scores ?? []).map((s) => [s.student_id, String(Number(s.score))])),
    comments: Object.fromEntries((assessment?.assessment_scores ?? []).map((s) => [s.student_id, s.comment ?? ''])),
  };
}

// `draftStorageKey` is where unsaved entries are backed up in the browser as they're typed, so a
// power cut or reload doesn't lose them; they're restored the next time this form is opened.
function AssessmentFormModal({
  config,
  assessment,
  defaultTerm,
  defaultSection,
  sections,
  roster,
  draftStorageKey,
  onClose,
  onSubmit,
}) {
  const [baseline] = useState(() => initialValues(assessment, defaultTerm, defaultSection));
  const [restored, setRestored] = useState(() => readDraft(draftStorageKey));
  const start = restored ? { ...baseline, ...restored.data.values } : baseline;

  const [title, setTitle] = useState(start.title);
  const [term, setTerm] = useState(start.term);
  const [maxScore, setMaxScore] = useState(start.maxScore);
  const [date, setDate] = useState(start.date);
  const [scores, setScores] = useState(start.scores);
  const [comments, setComments] = useState(start.comments);
  // The student whose comment is open in its own box, and the text typed there so far.
  const [commentStudent, setCommentStudent] = useState(
    () => roster.find((s) => s.id === restored?.data.openComment?.studentId) ?? null
  );
  const [commentDraft, setCommentDraft] = useState(restored?.data.openComment?.text ?? '');
  // An edited assessment's section is fixed, so a draft can't move it.
  const [section, setSection] = useState(assessment?.section ? baseline.section : start.section);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const values = { title, term, maxScore, date, section, scores, comments };
  const openComment = commentStudent ? { studentId: commentStudent.id, text: commentDraft } : null;
  const isDirty =
    JSON.stringify(values) !== JSON.stringify(baseline) ||
    Boolean(openComment && openComment.text !== (comments[openComment.studentId] ?? ''));

  // Backs up every change; once nothing differs from what's saved, the backup goes.
  const draftJson = JSON.stringify({ values, openComment });
  useEffect(() => {
    if (isDirty) writeDraft(draftStorageKey, JSON.parse(draftJson));
    else clearDraft(draftStorageKey);
  }, [draftStorageKey, draftJson, isDirty]);

  // A restored comment box resumes its typed text only the first time it's shown.
  const closeComment = () => {
    setCommentStudent(null);
    setRestored((r) => r && { ...r, data: { ...r.data, openComment: null } });
  };

  const discardDraft = () => {
    clearDraft(draftStorageKey);
    setRestored(null);
    setTitle(baseline.title);
    setTerm(baseline.term);
    setMaxScore(baseline.maxScore);
    setDate(baseline.date);
    setSection(baseline.section);
    setScores(baseline.scores);
    setComments(baseline.comments);
    setCommentStudent(null);
    setError('');
  };

  // Closing on purpose discards the unsaved entries, so that's confirmed first.
  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirmAction({
        title: 'Discard unsaved entries?',
        text: 'The scores and comments you entered in this form have not been saved yet.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        danger: true,
      });
      if (!confirmed) return;
    }
    clearDraft(draftStorageKey);
    onClose();
  };

  // An assessment belongs to one section, so scores are entered for that section's students only.
  // Assessments saved before sections were tracked have none and keep the whole roster.
  const isLegacy = Boolean(assessment) && !assessment.section;
  const sectionRoster = section ? roster.filter((s) => s.section === section) : isLegacy ? roster : [];
  const canSave = sections.length > 0 || isLegacy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!section && !isLegacy) {
      setError('Select a section.');
      return;
    }

    const max = Number(maxScore);
    if (!(max > 0)) {
      setError('Enter a maximum score greater than 0.');
      return;
    }

    // Blank means "not graded", so it's left out rather than saved as 0.
    const entered = [];
    for (const student of sectionRoster) {
      const raw = (scores[student.id] ?? '').trim();
      const comment = (comments[student.id] ?? '').trim();
      if (raw === '') {
        // A comment hangs off a score, so one without a score would be silently lost.
        if (comment) {
          setError(`${student.lastname}, ${student.firstname}: enter a score to save the comment.`);
          return;
        }
        continue;
      }

      const score = Number(raw);
      if (!Number.isFinite(score) || score < 0 || score > max) {
        setError(`${student.lastname}, ${student.firstname}: score must be between 0 and ${max}.`);
        return;
      }
      entered.push({ student_id: student.id, score, comment });
    }

    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        term: Number(term),
        section: section || null,
        maxScore: max,
        assessmentDate: date,
        scores: entered,
      });
      clearDraft(draftStorageKey);
    } catch (err) {
      setError(err.message || `Failed to save ${config.itemLabel}`);
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        title={assessment ? `Edit ${config.itemLabel}` : config.addLabel}
        onClose={handleClose}
        maxWidthClass="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          {restored && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              <span>Restored unsaved entries from {formatDraftTime(restored.savedAt)}. Save to keep them.</span>
              <button type="button" onClick={discardDraft} className="font-medium underline hover:text-amber-900">
                Discard
              </button>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}

          <div className="space-y-1">
            <label htmlFor="title" className="text-sm font-medium text-gray-700">Title</label>
            <input
              id="title"
              type="text"
              required
              placeholder={`e.g. ${config.itemLabel} 1`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            <div className="space-y-1">
              <label htmlFor="term" className="text-sm font-medium text-gray-700">Term</label>
              <select
                id="term"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="w-full min-w-0 rounded-md border border-gray-300 px-2 sm:px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                {TERMS.map((t) => (
                  <option key={t} value={t}>Term {t}</option>
                ))}
              </select>
            </div>

            {sections.length > 0 && (
              <div className="space-y-1">
                <label htmlFor="section" className="text-sm font-medium text-gray-700">Section</label>
                <select
                  id="section"
                  required={!isLegacy}
                  disabled={Boolean(assessment?.section)}
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  className="w-full min-w-0 rounded-md border border-gray-300 px-2 sm:px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">{isLegacy ? 'All Sections' : 'Select'}</option>
                  {sections.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="maxScore" className="text-sm font-medium text-gray-700">Max Score</label>
              <input
                id="maxScore"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={maxScore}
                onChange={(e) => setMaxScore(e.target.value)}
                className="w-full min-w-0 rounded-md border border-gray-300 px-2 sm:px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              />
            </div>

            <div className="space-y-1 col-span-3 sm:col-span-1 min-w-0">
              <label htmlFor="assessmentDate" className="text-sm font-medium text-gray-700">Date</label>
              <input
                id="assessmentDate"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="block w-full min-w-0 max-w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              />
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Scores</p>
            <p className="text-xs text-gray-500">
              Leave a score blank if the student isn't graded yet; enter 0 for a score of zero. A comment is
              optional and is shown to the student.
            </p>
            {sectionRoster.length === 0 ? (
              <p className="text-sm text-gray-500 rounded-md border border-gray-200 px-3 py-4 text-center">
                {sections.length === 0
                  ? 'No sections are assigned to this subject yet. Ask an admin to assign them in Subjects.'
                  : !section
                    ? 'Select a section to enter its scores.'
                    : 'No students found in this section.'}
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 font-medium">Name</th>
                      <th className="px-3 py-1.5 font-medium w-32">Score{maxScore ? ` / ${maxScore}` : ''}</th>
                      <th className="px-3 py-1.5 font-medium">Comment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sectionRoster.map((student) => (
                      <tr key={student.id}>
                        <td className="px-3 py-1.5 text-gray-700">
                          {student.lastname}, {student.firstname} {student.middlename || ''}
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Score for ${student.lastname}, ${student.firstname}`}
                            value={scores[student.id] ?? ''}
                            onChange={(e) => setScores((prev) => ({ ...prev, [student.id]: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => setCommentStudent(student)}
                            aria-label={`Comment for ${student.lastname}, ${student.firstname}`}
                            className={`flex w-full min-w-40 max-w-56 items-center gap-1.5 rounded-md border px-2 py-1 text-left text-sm hover:border-[#4ea6d0] ${
                              comments[student.id]
                                ? 'border-gray-300 text-gray-700'
                                : 'border-dashed border-gray-300 text-gray-400'
                            }`}
                          >
                            <ChatBubbleLeftEllipsisIcon className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {comments[student.id] ? comments[student.id].replace(/\s*\n\s*/g, ' ') : 'Add comment'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !canSave}
              className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {commentStudent && (
        <CommentModal
          studentName={`${commentStudent.lastname}, ${commentStudent.firstname}`}
          value={comments[commentStudent.id] ?? ''}
          initialDraft={restored?.data.openComment?.studentId === commentStudent.id ? commentDraft : undefined}
          onDraftChange={setCommentDraft}
          onClose={closeComment}
          onSave={(text) => {
            setComments((prev) => ({ ...prev, [commentStudent.id]: text }));
            closeComment();
          }}
        />
      )}
    </>
  );
}

export default AssessmentFormModal;
